import React, { useMemo } from 'react';
import { useDocumentTypes } from '../../../hooks/useDocumentTypes';

interface CarDocumentUploadProps {
  documentType: string;
  licenseFileName: string;
  onDocumentTypeChange: (value: string) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const documentTypeOptions = [
  { value: 'license', label: 'Vehicle License (رخصة السيارة)' },
  { value: 'registration', label: 'Registration (التسجيل)' },
  { value: 'insurance', label: 'Insurance (التأمين)' },
  { value: 'inspection', label: 'Inspection Certificate (شهادة الفحص)' },
  { value: 'ownership', label: 'Ownership Document (وثيقة الملكية)' },
  { value: 'other', label: 'Other (أخرى)' },
];

const CarDocumentUpload: React.FC<CarDocumentUploadProps> = ({
  documentType,
  licenseFileName,
  onDocumentTypeChange,
  onFileChange
}) => {
  const { data: documentTypes = [] } = useDocumentTypes({ pagination: { pageSize: 100 } });

  // Car document types configured in Document Settings appear alongside the
  // built-in options. Matching ignores case and parenthetical qualifiers so
  // a configured "Vehicle License" doesn't duplicate the built-in option.
  const mergedOptions = useMemo(() => {
    const key = (n: string) => n.replace(/\s*\([^)]*\)/g, '').trim().toLowerCase();
    const known = new Set(documentTypeOptions.map(o => key(o.label)));
    const extra = (documentTypes as any[])
      .filter(dt => dt.category === 'car' && dt.name && !known.has(key(dt.name)))
      .map(dt => ({ value: dt.name, label: dt.name }));
    return [...documentTypeOptions, ...extra];
  }, [documentTypes]);

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type (نوع الوثيقة)
        </label>
        <select
          value={documentType}
          onChange={(e) => onDocumentTypeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select Document Type</option>
          {mergedOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="col-span-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Document File (صورة الوثيقة)
        </label>
        <label htmlFor="licenseDocument" className="flex justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
          {licenseFileName ? (
            <span className="flex items-center space-x-2">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium text-gray-900">{licenseFileName}</span>
            </span>
          ) : (
            <span className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="flex flex-col items-center">
                <span className="font-semibold text-gray-600">Click to upload</span>
                <span className="text-xs text-gray-500">Document file (PDF, PNG, JPG - Max 10MB)</span>
              </span>
            </span>
          )}
          <input
            type="file"
            id="licenseDocument"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onFileChange}
          />
        </label>
      </div>
    </>
  );
};

export default CarDocumentUpload;
